import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { isNetworkError } from '../../utils/networkError';
import {
  DEMO_WRITE_BLOCKED_CODE,
  DEMO_WRITE_BLOCKED_MESSAGE,
  notifyDemoWriteBlocked,
  shouldBlockDemoApiWrite,
} from '../../utils/demoMode';
import { rememberSessionPolicy } from '../../utils/sessionPolicy';

const API_BASE = import.meta.env.VITE_API_URL || '/api/v1';

const SESSION_KILL_CODES = new Set([
  'TENANT_SUSPENDED',
  'TENANT_DEACTIVATED',
  'TENANT_INACTIVE',
  'DEMO_EXPIRED',
  'DEMO_LOCKED',
]);

export const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

/** Single-flight refresh so concurrent 401s share one /auth/refresh call. */
let refreshPromise: Promise<string | null> | null = null;

function clearAuthStorage() {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  localStorage.removeItem('permissions');
}

function forceLoginRedirect(reason?: string) {
  clearAuthStorage();
  if (window.location.pathname.startsWith('/login')) return;
  const params = reason ? `?reason=${encodeURIComponent(reason)}` : '';
  window.location.href = `/login${params}`;
}

function responseErrorCode(error: AxiosError): string {
  const data = error.response?.data as { code?: string; message?: string } | undefined;
  return String(data?.code || '');
}

function isTenantSessionKill(error: AxiosError): boolean {
  const code = responseErrorCode(error);
  if (SESSION_KILL_CODES.has(code)) return true;
  const message = String((error.response?.data as { message?: string } | undefined)?.message || '').toLowerCase();
  return message.includes('tenant is suspended')
    || message.includes('tenant is deactivated')
    || message.includes('tenant not found or inactive');
}

async function refreshAccessToken(): Promise<string | null> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) return null;

    const tenantSlug = localStorage.getItem('tenantSlug') || 'demo';
    const { data } = await axios.post(
      `${API_BASE}/auth/refresh`,
      { refreshToken },
      {
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-Slug': tenantSlug,
        },
      },
    );

    const accessToken = data?.data?.accessToken as string | undefined;
    const nextRefresh = data?.data?.refreshToken as string | undefined;
    if (!accessToken) return null;

    localStorage.setItem('accessToken', accessToken);
    if (nextRefresh) localStorage.setItem('refreshToken', nextRefresh);
    rememberSessionPolicy(data?.data?.session);
    return accessToken;
  })().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
}

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = localStorage.getItem('accessToken');
  const tenantSlug = localStorage.getItem('tenantSlug');

  if (token) config.headers.Authorization = `Bearer ${token}`;
  if (tenantSlug) config.headers['X-Tenant-Slug'] = tenantSlug;
  else config.headers['X-Tenant-Slug'] = 'demo';
  const appLanguage = localStorage.getItem('appLanguage');
  if (appLanguage) config.headers['X-App-Language'] = appLanguage;

  if (shouldBlockDemoApiWrite(config.method, config.url)) {
    notifyDemoWriteBlocked();
    const error = new AxiosError(DEMO_WRITE_BLOCKED_MESSAGE);
    error.code = DEMO_WRITE_BLOCKED_CODE;
    error.config = config;
    error.response = {
      status: 403,
      statusText: 'Forbidden',
      data: { success: false, message: DEMO_WRITE_BLOCKED_MESSAGE, code: DEMO_WRITE_BLOCKED_CODE },
      headers: {},
      config,
    };
    return Promise.reject(error);
  }

  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    const url = originalRequest?.url || '';
    const isAuthRequest = /\/auth\/(login|otp|refresh|logout)/.test(url);

    if (isNetworkError(error) || error.response?.status === 503) {
      return Promise.reject(error);
    }

    // Suspended / deactivated / demo-locked tenants must leave the app immediately.
    if (
      !isAuthRequest
      && (error.response?.status === 401 || error.response?.status === 403)
      && isTenantSessionKill(error)
    ) {
      const code = responseErrorCode(error) || 'TENANT_INACTIVE';
      forceLoginRedirect(code);
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry && !isAuthRequest) {
      originalRequest._retry = true;

      try {
        const accessToken = await refreshAccessToken();
        if (accessToken) {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        if (refreshError instanceof AxiosError && isTenantSessionKill(refreshError)) {
          forceLoginRedirect(responseErrorCode(refreshError) || 'TENANT_INACTIVE');
          return Promise.reject(refreshError);
        }
        if (refreshError instanceof AxiosError && responseErrorCode(refreshError) === 'SESSION_IDLE_TIMEOUT') {
          forceLoginRedirect('SESSION_IDLE_TIMEOUT');
          return Promise.reject(refreshError);
        }
        // fall through to logout
      }

      forceLoginRedirect();
    }

    return Promise.reject(error);
  },
);

export interface ApiResponse<T = unknown> {
  success: boolean;
  message: string;
  data: T;
  meta?: { total: number; page: number; limit: number; totalPages: number };
}

export default api;
